/**
 * service-reporting-api + service-reporting-ml client.
 *
 * Wired against the controllers verified during Phase 7a:
 *   - apps/service-reporting-api/src/modules/reports/reporting.controller.ts
 *       GET  /v1/reporting/definitions
 *       POST /v1/reporting/run
 *       POST /v1/reporting/preview
 *       GET  /v1/reporting/runs
 *       GET  /v1/reporting/runs/:id
 *   - apps/service-reporting-api/src/modules/dashboards/dashboard.controller.ts
 *       GET  /v1/reporting/dashboards/:operatorId
 *       POST /v1/reporting/dashboards/tiles
 *       POST /v1/reporting/dashboards/tiles/:id/update
 *       POST /v1/reporting/dashboards/tiles/:id/delete
 *       POST /v1/reporting/dashboards/tiles/reorder
 *   - apps/service-reporting-api/src/modules/schedules/scheduled-report.controller.ts
 *       POST /v1/reporting/schedules
 *       GET  /v1/reporting/schedules
 *       GET  /v1/reporting/schedules/:id
 *       POST /v1/reporting/schedules/:id/{pause,resume,run-now}
 *   - apps/service-reporting-ml/src/modules/forecast/forecast.controller.ts
 *       POST /v1/reporting-ml/forecasts
 *       POST /v1/reporting-ml/forecasts/what-if
 *       GET  /v1/reporting-ml/forecasts/:id
 *
 * What's NOT here (intentionally — flagged as DeferredNotice in pages):
 *   - No `/v1/reporting/dashboard` aggregator for the reporting landing page
 *     (the screens prototype renders synthetic KPIs).
 *   - No `/v1/reporting/access-audit` listing — there is per-row AuditRead
 *     wrapping, but the read surface is part of the deferred service-audit
 *     read API (see security/AuditLogPage's note).
 *   - No `/v1/reporting/kpis` config registry — KPI thresholds live in
 *     localStorage as a working stub.
 *   - No `/v1/reporting/alerts` registry — same pattern.
 *   - No `/v1/reporting/data-mining` / `/v1/reporting/benchmarks` /
 *     `/v1/reporting/role-scoped` — surfaces are deferred.
 *   - No `/v1/reporting/exports` — pre-built report export buttons defer.
 */
import { env } from '../env';

import { request } from './client';

const apiBase = (): string => env.serviceReportingApiBaseUrl;
const mlBase = (): string => env.serviceReportingMlBaseUrl;

// ---------------------------------------------------------------------------
// Definitions / Runs
// ---------------------------------------------------------------------------

export interface ReportDefinitionWire {
  readonly reportKey: string;
  readonly displayName: string;
  readonly description: string;
  readonly category: string;
  readonly paramsSchema: Record<string, unknown>;
  readonly cacheTtlSeconds: number;
  readonly active: boolean;
}

export interface ReportRunWire {
  readonly id: string;
  readonly reportKey: string;
  readonly paramsHash: string;
  readonly params: Record<string, unknown>;
  readonly status: string;
  readonly result: Record<string, unknown> | null;
  readonly failureReason: string | null;
  readonly durationMs: number | null;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly expiresAt: string | null;
  readonly fromCache: boolean;
}

export interface ReportParams {
  readonly fromDate: string;
  readonly toDate: string;
  readonly operatorIds?: readonly number[];
}

export async function listReportDefinitions(
  category?: string,
): Promise<readonly ReportDefinitionWire[]> {
  return request<readonly ReportDefinitionWire[]>(apiBase(), '/v1/reporting/definitions', {
    method: 'GET',
    query: category !== undefined ? { category } : undefined,
  });
}

export async function runReport(
  reportKey: string,
  params: ReportParams,
): Promise<ReportRunWire> {
  return request<ReportRunWire>(apiBase(), '/v1/reporting/run', {
    method: 'POST',
    body: { reportKey, params },
  });
}

export async function previewReport(
  reportKey: string,
  params: ReportParams,
): Promise<ReportRunWire> {
  return request<ReportRunWire>(apiBase(), '/v1/reporting/preview', {
    method: 'POST',
    body: { reportKey, params },
  });
}

export async function listReportRuns(
  reportKey?: string,
): Promise<readonly ReportRunWire[]> {
  return request<readonly ReportRunWire[]>(apiBase(), '/v1/reporting/runs', {
    method: 'GET',
    query: reportKey !== undefined ? { reportKey } : undefined,
  });
}

export async function getReportRun(id: string): Promise<ReportRunWire> {
  return request<ReportRunWire>(
    apiBase(),
    `/v1/reporting/runs/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

// ---------------------------------------------------------------------------
// Dashboards / Tiles
// ---------------------------------------------------------------------------

export type TileVizKind = 'SINGLE_VALUE' | 'TIME_SERIES' | 'TABLE' | 'HEATMAP';

export interface DashboardTileWire {
  readonly id: string;
  readonly operatorId: number | null;
  readonly sequence: number;
  readonly reportKey: string;
  readonly params: Record<string, unknown>;
  readonly vizKind: string;
  readonly title: string;
  readonly lastRun: ReportRunWire | null;
}

export interface CreateTileBody {
  readonly operatorId: number | null;
  readonly sequence: number;
  readonly reportKey: string;
  readonly params: ReportParams;
  readonly vizKind: TileVizKind;
  readonly title: string;
}

export async function listDashboardTiles(
  operatorId: number | 'platform',
): Promise<readonly DashboardTileWire[]> {
  const seg = operatorId === 'platform' ? 'platform' : String(operatorId);
  return request<readonly DashboardTileWire[]>(
    apiBase(),
    `/v1/reporting/dashboards/${seg}`,
    { method: 'GET' },
  );
}

export async function createDashboardTile(body: CreateTileBody): Promise<DashboardTileWire> {
  return request<DashboardTileWire>(apiBase(), '/v1/reporting/dashboards/tiles', {
    method: 'POST',
    body,
  });
}

export async function updateDashboardTile(
  id: string,
  patch: Partial<CreateTileBody>,
): Promise<DashboardTileWire> {
  return request<DashboardTileWire>(
    apiBase(),
    `/v1/reporting/dashboards/tiles/${encodeURIComponent(id)}/update`,
    { method: 'POST', body: patch },
  );
}

export async function deleteDashboardTile(id: string): Promise<{ readonly ok: true }> {
  return request<{ readonly ok: true }>(
    apiBase(),
    `/v1/reporting/dashboards/tiles/${encodeURIComponent(id)}/delete`,
    { method: 'POST' },
  );
}

export async function reorderDashboardTiles(
  operatorId: number | null,
  tileIds: readonly string[],
): Promise<readonly DashboardTileWire[]> {
  return request<readonly DashboardTileWire[]>(
    apiBase(),
    '/v1/reporting/dashboards/tiles/reorder',
    { method: 'POST', body: { operatorId, tileIds } },
  );
}

// ---------------------------------------------------------------------------
// Scheduled reports
// ---------------------------------------------------------------------------

export interface ScheduledReportWire {
  readonly id: string;
  readonly reportKey: string;
  readonly params: Record<string, unknown>;
  readonly cron: string;
  readonly recipients: readonly string[];
  readonly active: boolean;
  readonly lastRunId: string | null;
  readonly lastRunAt: string | null;
}

export interface CreateScheduleBody {
  readonly reportKey: string;
  readonly params: ReportParams;
  readonly cron: string;
  readonly recipients: readonly string[];
}

export async function listScheduledReports(): Promise<readonly ScheduledReportWire[]> {
  return request<readonly ScheduledReportWire[]>(apiBase(), '/v1/reporting/schedules', {
    method: 'GET',
  });
}

export async function createScheduledReport(
  body: CreateScheduleBody,
): Promise<ScheduledReportWire> {
  return request<ScheduledReportWire>(apiBase(), '/v1/reporting/schedules', {
    method: 'POST',
    body,
  });
}

export async function pauseScheduledReport(id: string): Promise<ScheduledReportWire> {
  return request<ScheduledReportWire>(
    apiBase(),
    `/v1/reporting/schedules/${encodeURIComponent(id)}/pause`,
    { method: 'POST' },
  );
}

export async function resumeScheduledReport(id: string): Promise<ScheduledReportWire> {
  return request<ScheduledReportWire>(
    apiBase(),
    `/v1/reporting/schedules/${encodeURIComponent(id)}/resume`,
    { method: 'POST' },
  );
}

export async function runScheduledReportNow(id: string): Promise<ScheduledReportWire> {
  return request<ScheduledReportWire>(
    apiBase(),
    `/v1/reporting/schedules/${encodeURIComponent(id)}/run-now`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------------------
// B-slice 5: Dashboard aggregator, KPI definitions, Alerts, Ad-hoc queries
// ---------------------------------------------------------------------------

export interface ReportingDashboardWire {
  readonly definitionCount: number;
  readonly recentRunCount: number;
  readonly scheduledCount: number;
  readonly kpiDefinitionCount: number;
  readonly alertCount: number;
  readonly cacheHitRate: number;
  readonly generatedAt: string;
}

export interface KpiDefinitionWire {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly metricKey: string;
  readonly direction: string;
  readonly warnThreshold: number | null;
  readonly criticalThreshold: number | null;
  readonly unit: string;
  readonly active: boolean;
  readonly createdAt: string;
}

export interface CreateKpiDefinitionBody {
  readonly name: string;
  readonly description?: string;
  readonly metricKey: string;
  readonly direction?: 'HIGHER_BETTER' | 'LOWER_BETTER' | 'RANGE';
  readonly warnThreshold?: number | null;
  readonly criticalThreshold?: number | null;
  readonly unit?: string;
}

export interface KpiAlertWire {
  readonly id: string;
  readonly kpiDefinitionId: string;
  readonly severity: string;
  readonly recipients: readonly string[];
  readonly condition: string;
  readonly threshold: number;
  readonly notifyMode: string;
  readonly active: boolean;
  readonly lastTriggeredAt: string | null;
  readonly createdAt: string;
}

export interface CreateKpiAlertBody {
  readonly kpiDefinitionId: string;
  readonly severity?: 'INFO' | 'WARN' | 'CRITICAL';
  readonly recipients: readonly string[];
  readonly condition?: 'ABOVE' | 'BELOW';
  readonly threshold: number;
  readonly notifyMode?: 'ONCE' | 'EVERY_RUN' | 'DAILY_DIGEST';
}

export interface AdHocQueryRunWire {
  readonly id: string;
  readonly sourceKey: string;
  readonly params: Record<string, unknown>;
  readonly status: string;
  readonly result: Record<string, unknown> | null;
  readonly failureReason: string | null;
  readonly durationMs: number | null;
  readonly startedAt: string;
  readonly completedAt: string | null;
}

export async function getReportingDashboard(): Promise<ReportingDashboardWire> {
  return request<ReportingDashboardWire>(apiBase(), '/v1/reporting/dashboard', {
    method: 'GET',
  });
}

export async function listKpiDefinitions(): Promise<readonly KpiDefinitionWire[]> {
  return request<readonly KpiDefinitionWire[]>(apiBase(), '/v1/reporting/kpi-definitions', {
    method: 'GET',
  });
}

export async function createKpiDefinition(
  body: CreateKpiDefinitionBody,
): Promise<KpiDefinitionWire> {
  return request<KpiDefinitionWire>(apiBase(), '/v1/reporting/kpi-definitions', {
    method: 'POST',
    body,
  });
}

export async function listKpiAlerts(): Promise<readonly KpiAlertWire[]> {
  return request<readonly KpiAlertWire[]>(apiBase(), '/v1/reporting/alerts', {
    method: 'GET',
  });
}

export async function createKpiAlert(body: CreateKpiAlertBody): Promise<KpiAlertWire> {
  return request<KpiAlertWire>(apiBase(), '/v1/reporting/alerts', {
    method: 'POST',
    body,
  });
}

export async function runAdHocQuery(
  sourceKey: string,
  params: ReportParams,
): Promise<AdHocQueryRunWire> {
  return request<AdHocQueryRunWire>(apiBase(), '/v1/reporting/ad-hoc/queries', {
    method: 'POST',
    body: { sourceKey, params },
  });
}

export async function listAdHocQueryRuns(): Promise<readonly AdHocQueryRunWire[]> {
  return request<readonly AdHocQueryRunWire[]>(apiBase(), '/v1/reporting/ad-hoc/queries', {
    method: 'GET',
  });
}

// ---------------------------------------------------------------------------
// Forecasts (service-reporting-ml)
// ---------------------------------------------------------------------------

export interface ForecastPoint {
  readonly stepIndex: number;
  readonly value: number;
  readonly lower: number;
  readonly upper: number;
}

export interface ForecastRunWire {
  readonly id: string;
  readonly metric: string;
  readonly method: string;
  readonly forecast: readonly ForecastPoint[];
  readonly seasonLength: number;
  readonly alpha: number;
  readonly beta: number;
  readonly gamma: number;
  readonly confidenceLevel: number;
  readonly inSampleRms: number;
  readonly durationMs: number;
  readonly startedAt: string;
}

export interface RunForecastBody {
  readonly metric: string;
  readonly history: readonly number[];
  readonly horizon: number;
  readonly seasonLength?: number;
  readonly confidenceLevel?: number;
  readonly operatorId?: number;
}

export interface RunWhatIfBody {
  readonly baselineRunId: string;
  readonly perturbations: readonly { readonly stepIndex: number; readonly multiplier: number }[];
}

export async function runForecast(body: RunForecastBody): Promise<ForecastRunWire> {
  return request<ForecastRunWire>(mlBase(), '/v1/reporting-ml/forecasts', {
    method: 'POST',
    body,
  });
}

export async function runWhatIf(body: RunWhatIfBody): Promise<ForecastRunWire> {
  return request<ForecastRunWire>(mlBase(), '/v1/reporting-ml/forecasts/what-if', {
    method: 'POST',
    body,
  });
}

export async function getForecastRun(id: string): Promise<ForecastRunWire> {
  return request<ForecastRunWire>(
    mlBase(),
    `/v1/reporting-ml/forecasts/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

// ---------------------------------------------------------------------------
// Analytics extras (E2E customer-portal): backs the deferred-removal wave for
// `apps/web-staff/src/features/reporting/`. Wired against
// `apps/service-reporting-api/src/modules/analytics-extras/`.
// ---------------------------------------------------------------------------

export interface AccessAuditRowWire {
  readonly id: string;
  readonly when: string;
  readonly actor: string;
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly reportKey: string | null;
}

export interface ShareLinkWire {
  readonly id: string;
  readonly operatorId: number | null;
  readonly audience: string;
  readonly url: string;
  readonly token: string;
  readonly expiresAt: string;
  readonly active: boolean;
  readonly createdAt: string;
}

export interface CreateShareLinkBody {
  readonly operatorId: number | null;
  readonly audience: string;
  readonly expiresAt: string;
}

export interface ManagementKpiTileWire {
  readonly title: string;
  readonly metricKey: string;
  readonly value: number;
  readonly unit: string;
  readonly hint: string;
}

export interface ManagementKpiResponseWire {
  readonly tiles: readonly ManagementKpiTileWire[];
  readonly generatedAt: string;
}

export interface BenchmarkRowWire {
  readonly metric: string;
  readonly outlet: number;
  readonly peer: number;
  readonly national: number;
}

export interface BenchmarkResponseWire {
  readonly outletId: string;
  readonly rows: readonly BenchmarkRowWire[];
  readonly generatedAt: string;
}

export interface CohortWire {
  readonly id: string;
  readonly cohortKey: string;
  readonly label: string;
  readonly filterExpr: string;
  readonly size: number;
  readonly growthPct: number;
}

export interface CohortMemberWire {
  readonly memberId: string;
  readonly note: string;
}

export interface CohortDetailWire extends CohortWire {
  readonly members: readonly CohortMemberWire[];
}

export interface RoleDashboardWire {
  readonly id: string;
  readonly roleKey: string;
  readonly dashboardLabel: string;
  readonly tileCount: number;
  readonly active: boolean;
}

export interface DiffEntryWire {
  readonly path: string;
  readonly a: unknown;
  readonly b: unknown;
}

export interface DiffResponseWire {
  readonly aRunId: string;
  readonly bRunId: string;
  readonly reportKey: string;
  readonly entries: readonly DiffEntryWire[];
}

export interface ExportResponseWire {
  readonly id: string;
  readonly reportRunId: string;
  readonly format: string;
  readonly byteSize: number;
  readonly payload: string;
  readonly createdAt: string;
}

export async function listAccessAudit(limit = 50): Promise<readonly AccessAuditRowWire[]> {
  return request<readonly AccessAuditRowWire[]>(apiBase(), '/v1/reporting/access-audit', {
    method: 'GET',
    query: { limit: String(limit) },
  });
}

export async function listShareLinks(): Promise<readonly ShareLinkWire[]> {
  return request<readonly ShareLinkWire[]>(apiBase(), '/v1/reporting/dashboards/shares', {
    method: 'GET',
  });
}

export async function createShareLink(body: CreateShareLinkBody): Promise<ShareLinkWire> {
  return request<ShareLinkWire>(apiBase(), '/v1/reporting/dashboards/shares', {
    method: 'POST',
    body,
  });
}

export async function getManagementKpis(): Promise<ManagementKpiResponseWire> {
  return request<ManagementKpiResponseWire>(apiBase(), '/v1/reporting/management-kpis', {
    method: 'GET',
  });
}

export async function getBenchmarks(outletId: string): Promise<BenchmarkResponseWire> {
  return request<BenchmarkResponseWire>(apiBase(), '/v1/reporting/benchmarks', {
    method: 'GET',
    query: { outletId },
  });
}

export async function listCohorts(): Promise<readonly CohortWire[]> {
  return request<readonly CohortWire[]>(apiBase(), '/v1/reporting/cohorts', {
    method: 'GET',
  });
}

export async function getCohort(id: string): Promise<CohortDetailWire> {
  return request<CohortDetailWire>(
    apiBase(),
    `/v1/reporting/cohorts/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

export async function listRoleDashboards(): Promise<readonly RoleDashboardWire[]> {
  return request<readonly RoleDashboardWire[]>(apiBase(), '/v1/reporting/role-dashboards', {
    method: 'GET',
  });
}

export async function diffReportRuns(aId: string, bId: string): Promise<DiffResponseWire> {
  return request<DiffResponseWire>(
    apiBase(),
    `/v1/reporting/runs/${encodeURIComponent(aId)}/diff/${encodeURIComponent(bId)}`,
    { method: 'GET' },
  );
}

export async function exportReportRun(
  runId: string,
  format: 'CSV' | 'JSON' = 'CSV',
): Promise<ExportResponseWire> {
  return request<ExportResponseWire>(
    apiBase(),
    `/v1/reporting/runs/${encodeURIComponent(runId)}/export`,
    { method: 'POST', body: { format } },
  );
}
